import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PhotoPicker from "@/components/admin/PhotoPicker";
import { errMsg } from "@/lib/errors";
import {
  BedDouble,
  Plus,
  Trash2,
  Edit2,
  Image as ImageIcon,
  Library,
  X,
  Save,
  Users,
  Maximize2,
} from "lucide-react";

const LANGS = ["it", "en", "de", "fr"];
const LANG_LABEL = { it: "Italiano", en: "English", de: "Deutsch", fr: "Français" };

const emptyMl = () => ({ it: "", en: "", de: "", fr: "" });

export default function RoomManagementTab() {
  const [rooms, setRooms] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {kind: 'room'|'gallery', data}
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([api.get("/rooms?include_hidden=true"), api.get("/galleries")]);
      setRooms(r.data);
      setGalleries(g.data);
    } catch (err) {
      toast.error("Errore caricamento camere");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const deleteRoom = async (room) => {
    if (!window.confirm(`Eliminare definitivamente "${room.name?.it}"? Non ci devono essere prenotazioni attive.`)) return;
    try {
      await api.delete(`/admin/rooms/${room.id}`);
      toast.success("Camera eliminata");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Errore eliminazione"));
    }
  };

  return (
    <section data-testid="room-management-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A5F]">Gestione Camere &amp; Gallerie</h2>
          <p className="text-xs text-[#635F59]">Aggiungi, modifica o elimina le tipologie di alloggio + sezioni galleria</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="add-room-btn" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
          <Plus className="w-4 h-4 mr-2" /> Nuova camera
        </Button>
        <Button
          variant="outline"
          onClick={async () => {
            if (!window.confirm("Ricalcola la disponibilità giornaliera in base alle unità totali di ogni camera (utile dopo aver aumentato il numero di unità). Procedere?")) return;
            try {
              const r = await api.post("/admin/rooms/fix-availability");
              const updated = r.data?.updated || 0;
              if (updated === 0) {
                toast.success("Tutto a posto — nessuna camera da correggere");
              } else {
                const names = (r.data?.details || []).map((d) => `${d.name}: ${d.before ?? "—"} → ${d.after}`).join("\n");
                toast.success(`Aggiornate ${updated} camere:\n${names}`, { duration: 8000 });
              }
              load();
            } catch (e) {
              toast.error("Errore");
              console.warn("[rooms] fix-availability:", e);
            }
          }}
          data-testid="fix-rooms-availability-btn"
          className="text-sm"
          title="Allinea la disponibilità giornaliera a unità totali (utile se hai aumentato il numero di camere)"
        >
          🔧 Ripara disponibilità
        </Button>
      </div>

      {/* Rooms grid */}
      <h3 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
        <BedDouble className="w-5 h-5" /> Camere ({rooms.length})
      </h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {rooms.map((r) => (
          <RoomCard
            key={r.id}
            room={r}
            onEdit={() => setEditing({ kind: "room", data: r })}
            onDelete={() => deleteRoom(r)}
          />
        ))}
        {!loading && rooms.length === 0 && (
          <p className="text-[#635F59] col-span-full text-center py-6">Nessuna camera. Crea la prima con il pulsante "Nuova camera".</p>
        )}
      </div>

      {/* Galleries */}
      <h3 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
        <ImageIcon className="w-5 h-5" /> Gallerie struttura ({galleries.length})
      </h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {galleries.map((g) => (
          <GalleryCard key={g.id} gallery={g} onEdit={() => setEditing({ kind: "gallery", data: g })} />
        ))}
      </div>

      {/* Edit / Create Dialog */}
      {(editing || creating) && (
        <RoomEditDialog
          item={editing?.data}
          kind={editing?.kind}
          isCreate={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </section>
  );
}

function RoomCard({ room, onEdit, onDelete }) {
  const cover = room.images?.[0];
  return (
    <article className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden hover:shadow-md transition" data-testid={`room-card-${room.slug}`}>
      <div className="aspect-video bg-[#FDFBF7] relative">
        {cover ? (
          <img src={cover} alt={room.name?.it} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#635F59]">
            <ImageIcon className="w-10 h-10 opacity-30" />
          </div>
        )}
        <span className="absolute top-2 right-2 bg-white/90 backdrop-blur text-xs px-2 py-1 rounded-full font-semibold text-[#1E3A5F]">
          €{room.price_per_night}/notte
        </span>
        {room.images?.length > 0 && (
          <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
            {room.images.length} foto
          </span>
        )}
        {room.published === false && (
          <span
            className="absolute top-2 left-2 bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
            title="Camera nascosta dal sito pubblico — gestibile solo dall'admin"
            data-testid={`room-hidden-badge-${room.slug}`}
          >
            🙈 Nascosta nel sito
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-semibold text-[#1E3A5F] mb-1 leading-tight">{room.name?.it}</div>
        <div className="flex items-center gap-3 text-[11px] text-[#635F59] mb-2">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{room.capacity}</span>
          <span className="flex items-center gap-1"><Maximize2 className="w-3 h-3" />{room.size_sqm}m²</span>
          <span>·</span>
          <span>{room.units_count || 1}× unità</span>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-${room.slug}`} className="flex-1 h-8 text-xs">
            <Edit2 className="w-3 h-3 mr-1" /> Modifica
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} data-testid={`delete-${room.slug}`} className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </article>
  );
}

function GalleryCard({ gallery, onEdit }) {
  const cover = gallery.images?.[0];
  return (
    <article className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden flex" data-testid={`gallery-card-${gallery.slug}`}>
      <div className="w-32 h-32 bg-[#FDFBF7] relative shrink-0">
        {cover ? (
          <img src={cover} alt={gallery.name?.it} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#635F59]">
            <ImageIcon className="w-8 h-8 opacity-30" />
          </div>
        )}
      </div>
      <div className="flex-1 p-3 flex flex-col justify-between">
        <div>
          <div className="font-semibold text-[#1E3A5F] mb-1">{gallery.name?.it}</div>
          <div className="text-[11px] text-[#635F59]">{gallery.images?.length || 0} foto</div>
        </div>
        <Button size="sm" variant="outline" onClick={onEdit} className="h-8 text-xs">
          <Edit2 className="w-3 h-3 mr-1" /> Modifica
        </Button>
      </div>
    </article>
  );
}

function RoomEditDialog({ item, kind, isCreate, onClose, onSaved }) {
  const [tab, setTab] = useState("base");
  const [lang, setLang] = useState("it");
  const initial = item || {
    slug: "",
    name: emptyMl(),
    short_description: emptyMl(),
    description: emptyMl(),
    capacity: 2,
    beds: "",
    size_sqm: 15,
    price_per_night: 79,
    amenities: [],
    images: [],
    units_count: 1,
    default_units_available: null,
    guide_room_numbers: [],
    published: true,
  };
  const [form, setForm] = useState({
    ...initial,
    name: { ...emptyMl(), ...(initial.name || {}) },
    short_description: { ...emptyMl(), ...(initial.short_description || {}) },
    description: { ...emptyMl(), ...(initial.description || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [imageInput, setImageInput] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const isGallery = kind === "gallery";

  const setMl = (field, l, v) => setForm((p) => ({ ...p, [field]: { ...p[field], [l]: v } }));

  const addImage = () => {
    const url = imageInput.trim();
    if (!url) return;
    if (!url.startsWith("http")) {
      toast.error("Inserisci un URL valido (https://...)");
      return;
    }
    setForm((p) => ({ ...p, images: [...(p.images || []), url] }));
    setImageInput("");
  };

  const removeImage = (idx) => {
    setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, dir) => {
    setForm((p) => {
      const arr = [...p.images];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return p;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...p, images: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isGallery) {
        await api.put(`/admin/galleries/${item.id}`, {
          name: form.name,
          description: form.description,
          images: form.images,
        });
      } else if (isCreate) {
        if (!form.slug.trim()) { toast.error("Slug obbligatorio"); setSaving(false); return; }
        if (!form.name.it.trim()) { toast.error("Nome italiano obbligatorio"); setSaving(false); return; }
        await api.post("/admin/rooms", form);
      } else {
        // Final parse of guide_room_numbers in case the user typed but never blurred.
        const guideNumsSource = form.guide_room_numbers_text != null
          ? form.guide_room_numbers_text
          : (form.guide_room_numbers || []).join(", ");
        const guideNums = String(guideNumsSource)
          .split(",")
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => Number.isFinite(n));
        await api.put(`/admin/rooms/${item.id}`, {
          name: form.name,
          short_description: form.short_description,
          description: form.description,
          capacity: form.capacity,
          beds: form.beds,
          size_sqm: form.size_sqm,
          price_per_night: form.price_per_night,
          units_count: form.units_count,
          default_units_available: form.default_units_available,
          amenities: form.amenities,
          images: form.images,
          guide_room_numbers: guideNums,
          published: form.published !== false,
        });
      }
      toast.success("Salvato");
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A5F]">
            {isGallery ? "Modifica galleria" : isCreate ? "Nuova camera" : "Modifica camera"}
          </DialogTitle>
          <DialogDescription>
            {isGallery ? form.name?.it : (form.name?.it || form.slug || "Configura nome, foto e dettagli")}
          </DialogDescription>
        </DialogHeader>

        {/* Section tabs */}
        <div className="flex gap-1 border-b border-[#E8E2D9]">
          {[
            { id: "base", label: "Base" },
            ...(!isGallery ? [{ id: "details", label: "Dettagli" }] : []),
            { id: "texts", label: "Testi multilingua" },
            { id: "images", label: `Foto (${form.images?.length || 0})` },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setTab(s.id)}
              data-testid={`tab-${s.id}`}
              className={`px-3 py-2 text-sm border-b-2 transition ${tab === s.id ? "border-[#AC5D45] text-[#1E3A5F] font-semibold" : "border-transparent text-[#635F59] hover:text-[#1E3A5F]"}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="py-3 space-y-4">
          {tab === "base" && (
            <>
              {!isGallery && isCreate && (
                <div>
                  <Label>Slug (identificatore URL — solo lettere, numeri e trattini)</Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="es. monolocale-5" data-testid="form-slug" className="mt-1.5" />
                </div>
              )}
              <div>
                <Label>Nome (italiano) *</Label>
                <Input value={form.name.it} onChange={(e) => setMl("name", "it", e.target.value)} placeholder="Es. Monolocale Standard 5 posti" data-testid="form-name-it" className="mt-1.5" />
              </div>
              {!isGallery && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Prezzo €/notte</Label>
                      <Input type="number" min="0" step="0.5" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: parseFloat(e.target.value) || 0 })} data-testid="form-price" className="mt-1.5" />
                    </div>
                    <div>
                      <Label>Unità totali</Label>
                      <Input type="number" min="1" max="20" value={form.units_count} onChange={(e) => setForm({ ...form, units_count: parseInt(e.target.value) || 1 })} data-testid="form-units" className="mt-1.5" />
                      <p className="text-[10px] text-[#635F59] mt-1">Numero massimo fisico di stanze di questa categoria.</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="flex items-center gap-2">
                      Unità in vendita (default)
                      <span className="text-[10px] font-normal text-[#635F59]">— quante esponi al pubblico ogni giorno</span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      placeholder={`Lascia vuoto per usare ${form.units_count || 1}`}
                      value={form.default_units_available ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm({ ...form, default_units_available: v === "" ? null : Math.max(0, parseInt(v) || 0) });
                      }}
                      data-testid="form-default-units-available"
                      className="mt-1.5 max-w-[200px]"
                    />
                    <p className="text-[10px] text-[#635F59] mt-1">
                      💡 Quante stanze di questa categoria vuoi rendere prenotabili ogni notte. Se vuoto, usa "Unità totali".
                      Quando arriva un check-in, viene decrementato di 1 — al raggiungimento di 0 la data diventa rossa
                      (esaurita) e il sito non venderà più quella camera per quella notte.
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {tab === "details" && !isGallery && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Capacità (ospiti)</Label>
                  <Input type="number" min="1" max="20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} data-testid="form-capacity" className="mt-1.5" />
                </div>
                <div>
                  <Label>Dimensione m²</Label>
                  <Input type="number" min="0" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: parseInt(e.target.value) || 0 })} data-testid="form-sqm" className="mt-1.5" />
                </div>
                <div>
                  <Label>Letti (testo)</Label>
                  <Input value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} placeholder="1 matrim. + 2 singoli" data-testid="form-beds" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Servizi (separati da virgola)</Label>
                <Input
                  value={(form.amenities || []).join(", ")}
                  onChange={(e) => setForm({ ...form, amenities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="wifi, tv, air_conditioning, kitchen"
                  data-testid="form-amenities"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Numeri camera (App Guida)
                  <span className="text-[10px] font-normal text-[#635F59]">— per sync automatico walk-in</span>
                </Label>
                <Input
                  // Keep the raw typed string in `guide_room_numbers_text` so the
                  // user can type commas, spaces, partial numbers freely. We
                  // only parse to integers on save.
                  value={form.guide_room_numbers_text ?? (form.guide_room_numbers || []).join(", ")}
                  onChange={(e) => setForm({ ...form, guide_room_numbers_text: e.target.value })}
                  onBlur={(e) => {
                    const nums = e.target.value
                      .split(",")
                      .map((s) => parseInt(s.trim(), 10))
                      .filter((n) => Number.isFinite(n));
                    setForm({ ...form, guide_room_numbers: nums, guide_room_numbers_text: nums.join(", ") });
                  }}
                  inputMode="numeric"
                  placeholder="es. 13, 14"
                  data-testid="form-guide-room-numbers"
                  className="mt-1.5"
                />
                <p className="text-[10px] text-[#635F59] mt-1">
                  💡 I numeri fisici delle stanze come usati dall'App Guida (es. <code>13</code>, <code>15</code>).
                  Quando arriva un check-in dall'app con quel <code>room_number</code>, la prenotazione viene
                  assegnata automaticamente a questa camera.
                </p>
              </div>
              {/* Visibility toggle — keep aux rooms (e.g. bagno-esterno) out
                  of the public site while still letting the admin use them
                  for multi-room proposals, blocks, etc. */}
              <div className="rounded-lg border border-[#E8E2D9] bg-[#FDFBF7] p-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.published !== false}
                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    className="w-5 h-5 accent-[#AC5D45]"
                    data-testid="form-published-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1E3A5F]">
                      Mostra nel sito pubblico
                    </div>
                    <div className="text-[10px] text-[#635F59] mt-0.5">
                      Se disattivato, la camera non appare nelle pagine pubbliche né nel motore di prenotazione,
                      ma resta gestibile nel PMS (blocchi, walk-in, proposte multi-camera).
                    </div>
                  </div>
                </label>
              </div>
            </>
          )}

          {tab === "texts" && (
            <>
              <div className="flex gap-1 mb-3">
                {LANGS.map((l) => (
                  <button key={l} type="button" onClick={() => setLang(l)} className={`px-3 py-1.5 rounded text-xs font-semibold ${lang === l ? "bg-[#1E3A5F] text-white" : "bg-[#FDFBF7] border border-[#E8E2D9] text-[#635F59]"}`}>
                    {LANG_LABEL[l]}
                  </button>
                ))}
              </div>
              <div>
                <Label>Nome ({LANG_LABEL[lang]})</Label>
                <Input value={form.name[lang] || ""} onChange={(e) => setMl("name", lang, e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Descrizione breve ({LANG_LABEL[lang]})</Label>
                <Input value={form.short_description?.[lang] || ""} onChange={(e) => setMl("short_description", lang, e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Descrizione lunga ({LANG_LABEL[lang]})</Label>
                <Textarea value={form.description?.[lang] || ""} onChange={(e) => setMl("description", lang, e.target.value)} rows={4} className="mt-1.5" />
              </div>
            </>
          )}

          {tab === "images" && (
            <>
              <div className="grid sm:grid-cols-3 gap-2">
                <label
                  className="flex items-center justify-center gap-2 h-10 px-4 rounded-md cursor-pointer bg-[#1E3A5F] text-white hover:bg-[#152A45] transition text-sm font-semibold"
                  data-testid="form-image-upload-label"
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    multiple
                    className="hidden"
                    data-testid="form-image-file-input"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      e.target.value = "";
                      if (files.length === 0) return;
                      const uploaded = [];
                      for (const file of files) {
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          if (isGallery) {
                            fd.append("category", "gallery");
                          } else {
                            fd.append("category", "rooms");
                            const roomTag = (form.name?.it || "").trim().toLowerCase();
                            if (roomTag) fd.append("tags", roomTag);
                          }
                          const res = await api.post("/admin/upload-photo", fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          uploaded.push(res.data.url);
                        } catch (err) {
                          toast.error(`${file.name}: ${errMsg(err, "errore")}`);
                        }
                      }
                      if (uploaded.length > 0) {
                        setForm((p) => ({ ...p, images: [...(p.images || []), ...uploaded] }));
                        toast.success(`${uploaded.length} foto caricate`);
                      }
                    }}
                  />
                  <Plus className="w-4 h-4" />
                  Carica file dal computer
                </label>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#AC5D45] text-white hover:bg-[#8A4936] transition text-sm font-semibold"
                  data-testid="form-image-pick-from-library"
                  title="Scegli foto già caricate dall'archivio centrale"
                >
                  <Library className="w-4 h-4" />
                  Scegli dall'archivio
                </button>
                <div className="flex gap-2">
                  <Input value={imageInput} onChange={(e) => setImageInput(e.target.value)} placeholder="…oppure incolla URL" data-testid="form-image-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }} />
                  <Button type="button" onClick={addImage} data-testid="form-image-add" variant="outline" className="shrink-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Open in a new TOP-level window to escape the Emergent preview iframe.
                    // Without this, Google Drive returns ERR_BLOCKED_BY_RESPONSE inside iframes.
                    try {
                      const w = window.open("about:blank", "_blank", "noopener,noreferrer");
                      if (w) {
                        w.location.href = "https://drive.google.com/drive/my-drive";
                      } else {
                        window.open("https://drive.google.com/drive/my-drive", "_blank", "noopener,noreferrer");
                      }
                    } catch {
                      window.open("https://drive.google.com/drive/my-drive", "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-white border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FDFBF7] transition text-sm font-semibold w-full"
                  data-testid="form-image-open-gdrive"
                  title="Apri Google Drive in una nuova scheda · poi torna qui e usa 'Carica file dal computer' per caricare"
                >
                  <svg className="w-4 h-4" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                  </svg>
                  Apri Google Drive
                </button>
              </div>
              <p className="text-[11px] text-[#635F59]">
                Carica più foto contemporaneamente. La prima è quella di copertina (puoi riordinare con ← →). <strong>Da Google Drive</strong>: apri Drive, scarica la foto sul dispositivo, poi clicca "Carica file dal computer".
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(form.images || []).map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group aspect-video rounded overflow-hidden border border-[#E8E2D9]" data-testid={`form-image-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 text-[9px] bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded font-semibold">COVER</span>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                      {i > 0 && <button onClick={() => moveImage(i, -1)} className="bg-white/90 rounded px-1.5 py-1 text-xs">←</button>}
                      {i < (form.images.length - 1) && <button onClick={() => moveImage(i, 1)} className="bg-white/90 rounded px-1.5 py-1 text-xs">→</button>}
                      <button onClick={() => removeImage(i)} className="bg-rose-600 text-white rounded px-1.5 py-1 text-xs"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                {(form.images || []).length === 0 && (
                  <p className="col-span-full text-center text-[#635F59] py-6 text-sm">Nessuna foto. Carica file o incolla un URL.</p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="form-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <PhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        multiple
        defaultCategory={isGallery ? "gallery" : "rooms"}
        onSelect={(urls) => {
          const arr = Array.isArray(urls) ? urls : [urls];
          setForm((p) => ({ ...p, images: [...(p.images || []), ...arr] }));
          toast.success(`${arr.length} foto aggiunte`);
        }}
      />
    </Dialog>
  );
}
